import { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../contexts/authContext";
import { deleteCustomaizedAPI, getCustomizedAPI, postCustomizedAPI, updateCustomizedAPI } from "../../../nodeApi/cruds";
import { deleteAPI, getAPI, postAPI, putAPI } from "../../../apis/backend/apiNode";

export const useLogic = () => {
  const { userData } = useContext(AuthContext);
  const token = userData?.accessToken;
  const userId = userData?.id;
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(false);
  const [spinner, setSpinner] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [searchType, setSearchType] = useState('nom'); // Default search by name
  const [advancedSearchCriteria, setAdvancedSearchCriteria] = useState({});

  // Modals
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);

  // Load clients data with search functionality
  const loadData = async (page = 1, searchParams = {}) => {
    setSpinner(true);
    setError(false);

    try {
      let url = `?page=${page}&limit=${perPage}`;

      // Add search parameters based on search type
      if (filter.trim()) {
        if (searchType === 'nom') {
          url += `&nom=${encodeURIComponent(filter)}`;
        } else if (searchType === 'telephone') {
          url += `&telephone=${encodeURIComponent(filter)}`;
        } else if (searchType === 'advanced') {
          // Use advanced search endpoint
          return await performAdvancedSearch(page, searchParams);
        }
      }

      // Add status filter if selected
      if (selectedStatus) {
        url += `&statut=${encodeURIComponent(selectedStatus)}`;
      }

      const response = await getAPI("store2000_client", url);

      if (response.success) {
        setData(response.clients || []);
        setPagination(response.pagination || {});
        setTotalPages(response.pagination?.totalPages || 1);
      } else {
        setError(true);
        console.error('Error loading clients:', response.error);
      }
    } catch (err) {
      setError(true);
      console.error('Error loading clients:', err);
    } finally {
      setSpinner(false);
    }
  };

  // Advanced search function
  const performAdvancedSearch = async (page = 1, criteria = {}) => {
    try {
      let url = `/search/advanced?page=${page}&limit=${perPage}`;

      // Add advanced search criteria
      const params = new URLSearchParams();
      Object.keys(criteria).forEach(key => {
        if (criteria[key]) {
          params.append(key, criteria[key]);
        }
      });

      const queryString = params.toString();
      if (queryString) {
        url += `&${queryString}`;
      }

      const response = await getAPI("store2000_client", url);

      if (response.success) {
        setData(response.clients || []);
        setPagination(response.pagination || {});
        setTotalPages(response.pagination?.totalPages || 1);
        setAdvancedSearchCriteria(criteria);
      } else {
        setError(true);
        console.error('Error in advanced search:', response.error);
      }
    } catch (err) {
      setError(true);
      console.error('Error in advanced search:', err);
    } finally {
      setSpinner(false);
    }
  };
  //delete 
  const deleteClient = async (clientId) => {
    try {
      const response = await deleteAPI("store2000_client", `/${clientId}`,);

      if (response.success) {
        return { success: true, message: response.message }
      } else {
        return { success: false, error: err.error }
      }
    } catch (err) {
      return { success: false, error: err.error }
    }
  }
  // Search by phone number specifically
  const searchByPhone = async (phoneNumber, page = 1) => {
    setSpinner(true);
    try {
      // Clean phone number (remove spaces, special characters)
      const cleanPhone = phoneNumber.replace(/\D/g, '');

      const response = await getAPI(
        "store2000_client",
        `/phone/${cleanPhone}?page=${page}&limit=${perPage}`
      );

      if (response.success) {
        setData(response.clients || []);
        setPagination(response.pagination || {});
        setTotalPages(response.pagination?.totalPages || 1);
        setSearchType('telephone');
        setFilter(phoneNumber);
      } else {
        setError(true);
        console.error('Error searching by phone:', response.error);
      }
    } catch (err) {
      setError(true);
      console.error('Error searching by phone:', err);
    } finally {
      setSpinner(false);
    }
  };

  // Create new client
  const createClient = async (clientData) => {
    try {
      const response = await postAPI('store2000_client', clientData, "");

      if (response.success) {
        await loadData(currentPage);
        return { success: true, data: response.client };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      return {
        success: false,
        error: err?.response?.data.error || 'Erreur de connexion au serveur.',
      };
    }
  };

  // Get client by ID
  const getClientById = async (clientId) => {
    try {
      const response = await getAPI("store2000_client", `/${clientId}`);

      if (response.success) {
        return { success: true, data: response.client };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Update client
  const updateClient = async (clientId, updateData) => {
    try {
      const response = await putAPI(`store2000_client`, updateData, `/${clientId}`);

      if (response.success) {
        await loadData(currentPage);
        return { success: true, data: response.client };
      } else {
        return { success: false, error: response.error };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    if (searchType === 'advanced') {
      performAdvancedSearch(newPage, advancedSearchCriteria);
    } else if (searchType === 'telephone' && filter) {
      searchByPhone(filter, newPage);
    } else {
      loadData(newPage);
    }
  };

  // Handle per page change
  const handlePerPageChange = (event) => {
    const newPerPage = parseInt(event.target.value, 10);
    setPerPage(newPerPage);
    setCurrentPage(1);
    loadData(1);
  };

  // Handle filter change
  const handleFilterChange = (filterValue) => {
    setFilter(filterValue);
    setCurrentPage(1);
  };

  // Handle search type change
  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setFilter('');
    setCurrentPage(1);
    setAdvancedSearchCriteria({});

    // Reset to normal load if switching from advanced search
    if (type !== 'advanced') {
      loadData(1);
    }
  };

  // Handle status filter change
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilter('');
    setSelectedStatus('');
    setSearchType('nom');
    setAdvancedSearchCriteria({});
    setCurrentPage(1);
    loadData(1);
  };

  // Perform advanced search with multiple criteria
  const handleAdvancedSearch = (criteria) => {
    setSearchType('advanced');
    setCurrentPage(1);
    performAdvancedSearch(1, criteria);
  };

  // Format phone number for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return 'Pas de numéro';

    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
    }
    return phone;
  };

  // Get status badge color
  const getStatusColor = (statut) => {
    const colors = {
      'prospect': 'warning',
      'intéressé': 'info',
      'devis': 'primary',
      'client': 'success',
      'inactif': 'secondary',
      'refusé': 'danger'
    };
    return colors[statut] || 'secondary';
  };

  useEffect(() => {
    if (searchType === 'advanced' && Object.keys(advancedSearchCriteria).length > 0) {
      performAdvancedSearch(currentPage, advancedSearchCriteria);
    } else if (searchType === 'telephone' && filter) {
      searchByPhone(filter, currentPage);
    } else {
      loadData(currentPage);
    }
  }, [filter, perPage, selectedStatus, currentPage]);

  return {
    // State
    data,
    pagination,
    filter,
    currentPage,
    perPage,
    totalPages,
    error,
    spinner,
    selectedStatus,
    searchType,
    advancedSearchCriteria,

    // Authentication
    token,
    userId,
    navigate,

    // Methods
    loadData,
    createClient,
    getClientById,
    updateClient,
    searchByPhone,
    performAdvancedSearch,
    deleteClient,
    // Handlers
    handlePageChange,
    handlePerPageChange,
    handleFilterChange,
    handleSearchTypeChange,
    handleStatusChange,
    handleAdvancedSearch,
    clearFilters,

    // Modals
    openCreateModal,
    setOpenCreateModal,
    openUpdateModal,
    setOpenUpdateModal,

    // Utilities
    formatPhoneDisplay,
    getStatusColor
  };
};